import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { getPublicSiteCode } from "@Front/themes/siteTheme";
import { normalizeLegacyDeploymentSiteCode } from "le-coffre-resources/dist/deploymentSiteCodes.js";
import { multisiteBrandingAbsoluteUrlTargetsForeignLine } from "le-coffre-resources/dist/browserPublicHostPatterns.js";
import type { LoadedPublicSiteConfig } from "./publicSiteConfigLoaderTypes";

function resolveDeploymentSiteCodeForBrandingAssert(cfg: LoadedPublicSiteConfig): string | null {
	const fromApi = typeof cfg.site_code === "string" ? cfg.site_code.trim() : "";
	if (fromApi.length > 0) {
		return normalizeLegacyDeploymentSiteCode(fromApi);
	}
	try {
		return getPublicSiteCode();
	} catch {
		return null;
	}
}

/**
 * Supprime les entrées `branding` dont l’URL absolue cible une autre ligne ou un hôte vitrine cabinet non autorisé.
 */
export function assertLoadedPublicSiteConfigBrandingMatchesLine(cfg: LoadedPublicSiteConfig): LoadedPublicSiteConfig {
	const siteCode = resolveDeploymentSiteCodeForBrandingAssert(cfg);
	if (!siteCode) {
		LoggerService.warn("[PublicSiteConfig] Cannot resolve site line for branding assert (no site_code in API, NEXT_PUBLIC_SITE_CODE missing)");
		return cfg;
	}
	const b = cfg.branding;
	if (!b) {
		return cfg;
	}
	const stripIfForeign = (field: string, u: string | null | undefined): string | null => {
		if (u == null || typeof u !== "string" || u.trim().length === 0) {
			return null;
		}
		if (multisiteBrandingAbsoluteUrlTargetsForeignLine(u, siteCode)) {
			LoggerService.error("[PublicSiteConfig] Branding URL targets another line or forbidden host — dropping field", {
				field,
				siteCode,
				url: u,
			});
			return null;
		}
		return u;
	};
	const logo_main_url = stripIfForeign("logo_main_url", b.logo_main_url);
	const logo_small_url = stripIfForeign("logo_small_url", b.logo_small_url);
	const favicon_url = stripIfForeign("favicon_url", b.favicon_url);
	const isEmpty = (v: string | null): boolean => v == null || v.trim().length === 0;
	if (isEmpty(logo_main_url) && isEmpty(logo_small_url) && isEmpty(favicon_url)) {
		const { branding: _b, ...rest } = cfg;
		return { ...rest };
	}
	return {
		...cfg,
		branding: { logo_main_url, logo_small_url, favicon_url },
	};
}
